/**
 * @file pages/Dashboard.jsx
 * @description Main dashboard page.
 *
 * Displays four summary metric cards:
 *  - Total active tokens
 *  - Logged endpoints (internal endpoint configs + proxy routes with logging enabled)
 *  - Total log entries
 *  - Average response time across all logged requests
 *
 * Below the cards a recent activity list shows the last 10 request log entries
 * (list layout, not a wide table, so it doesn't overflow horizontally on narrow screens).
 */

import React, { useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import MetricsCard from '../components/MetricsCard';
import { useSocket } from '../context/SocketContext';
import { listTokens }         from '../api/tokens';
import { listEndpointConfigs } from '../api/endpointConfigs';
import { listProxyRoutes }    from '../api/proxyRoutes';
import { listLogs, getMetrics } from '../api/logs';

/**
 * Formats a millisecond duration to a human-readable string.
 * @param {number|null} ms
 * @returns {string}
 */
function formatMs(ms) {
  if (ms == null || isNaN(ms)) return '—';
  return `${Math.round(ms)} ms`;
}

/**
 * Returns a Bootstrap badge variant for an HTTP status code.
 * @param {number} status
 */
function statusVariant(status) {
  if (status < 300) return 'success';
  if (status < 500) return 'warning';
  return 'danger';
}

/**
 * Dashboard page component.
 * All data is fetched in parallel to minimise loading time.
 */
export default function Dashboard() {
  const queryClient = useQueryClient();
  const { socket }   = useSocket();

  // Fetch all data sources in parallel
  const { data: tokens }   = useQuery({ queryKey: ['tokens'],          queryFn: listTokens });
  const { data: configs }  = useQuery({ queryKey: ['endpointConfigs'], queryFn: listEndpointConfigs });
  const { data: proxyRoutes } = useQuery({ queryKey: ['proxyRoutes'],  queryFn: listProxyRoutes });
  const { data: logsPage } = useQuery({
    queryKey: ['logs', { limit: 10 }],
    queryFn:  () => listLogs({ limit: 10, page: 1 }),
  });
  const { data: metrics }  = useQuery({
    queryKey: ['metrics'],
    queryFn:  () => getMetrics(),
  });

  // Live auto-update: when the backend emits 'apiRequest:logged' (gated by the
  // Settings > Events > "Emit event on API requests" toggle), refresh the
  // recent-requests list and metrics so this page updates without a manual reload.
  useEffect(() => {
    if (!socket) return;

    const handleApiRequestLogged = () => {
      queryClient.invalidateQueries({ queryKey: ['logs', { limit: 10 }] });
      queryClient.invalidateQueries({ queryKey: ['metrics'] });
    };

    socket.on('apiRequest:logged', handleApiRequestLogged);
    return () => socket.off('apiRequest:logged', handleApiRequestLogged);
  }, [socket, queryClient]);

  // Derived summary values
  const activeTokens = tokens?.filter((t) => !t.revoked).length ?? '…';
  // Counts both internal endpoint configs and proxy routes with logging enabled
  const loggingEndpoints = configs && proxyRoutes
    ? configs.filter((c) => c.logging_enabled).length + proxyRoutes.filter((r) => r.logging_enabled).length
    : '…';
  const totalLogs        = logsPage?.total ?? '…';

  // Compute overall average duration from the per-endpoint metrics
  const avgDuration = (() => {
    if (!metrics || metrics.length === 0) return null;
    const totalRequests = metrics.reduce((sum, m) => sum + m.request_count, 0);
    if (totalRequests === 0) return null;
    const weightedSum   = metrics.reduce(
      (sum, m) => sum + m.avg_duration_ms * m.request_count, 0
    );
    return weightedSum / totalRequests;
  })();

  return (
    <div className="page-content">
      <h1 className="h4 fw-bold mb-4" style={{ color: 'var(--color-primary)' }}>
        <i className="bi bi-speedometer2 me-2" aria-hidden="true" />
        Dashboard
      </h1>

      {/* ── Summary metric cards ─────────────────────────────────────────── */}
      <div className="row g-3 mb-4">
        <div className="col-sm-6 col-xl-3">
          <MetricsCard
            title="Active Tokens"
            value={activeTokens}
            icon="bi-key-fill"
            accent="--color-primary"
          />
        </div>
        <div className="col-sm-6 col-xl-3">
          <MetricsCard
            title="Logged Endpoints"
            value={loggingEndpoints}
            icon="bi-toggles"
            accent="--color-accent-2"
          />
        </div>
        <div className="col-sm-6 col-xl-3">
          <MetricsCard
            title="Total Log Entries"
            value={typeof totalLogs === 'number' ? totalLogs.toLocaleString() : totalLogs}
            icon="bi-journal-text"
            accent="--color-accent-1"
          />
        </div>
        <div className="col-sm-6 col-xl-3">
          <MetricsCard
            title="Avg Response Time"
            value={formatMs(avgDuration)}
            icon="bi-stopwatch"
            accent="--color-accent-3"
          />
        </div>
      </div>

      {/* ── Recent activity ──────────────────────────────────────────────── */}
      <div className="card">
        <div
          className="card-header fw-semibold"
          style={{ backgroundColor: 'var(--color-primary)', color: 'var(--color-text-light)' }}
        >
          <i className="bi bi-clock-history me-2" aria-hidden="true" />
          Recent Requests
        </div>
        <div className="card-body p-0">
          <ul className="list-group list-group-flush">
            {!logsPage && (
              <li className="list-group-item text-center py-4">
                <div
                  className="spinner-border spinner-border-sm"
                  style={{ color: 'var(--color-primary)' }}
                  role="status"
                >
                  <span className="visually-hidden">Loading…</span>
                </div>
              </li>
            )}
            {logsPage && (logsPage.data ?? []).length === 0 && (
              <li className="list-group-item text-center text-muted py-4">
                No request logs recorded yet.
              </li>
            )}
            {(logsPage?.data ?? []).map((row) => (
              <li key={row.id} className="list-group-item">
                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                  <span className="text-muted small">{new Date(row.timestamp).toLocaleString()}</span>
                  <span
                    className="badge"
                    style={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
                  >
                    {row.method}
                  </span>
                  <code className="small text-break">{row.path}</code>
                  <span className={`badge bg-${statusVariant(row.response_status)} ms-auto`}>
                    {row.response_status}
                  </span>
                </div>
                <div className="d-flex flex-wrap gap-3 small text-muted">
                  <span>{formatMs(row.duration_ms)}</span>
                  <span>{row.bytes_out != null ? `${row.bytes_out} B out` : '—'}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
